import { booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';

import { rus } from '@models/utils/strings';

@Component({
    selector: 'vz-dt, [vz-dt]',
    template: `
        @if (dt(); as d) {
            <div [title]="title()">{{ text() }}</div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VzDtComponent {
    dt          = input<number | Date>();
    live        = input(true, { transform: booleanAttribute });
    noTime      = input(false, { transform: booleanAttribute });
    absolute    = input(false, { transform: booleanAttribute });

    text  = signal<string>('');
    title = signal<string>('');
    timer = signal<number>(0);

    timerSubscription?: any;
    private _destroy: DestroyRef = inject(DestroyRef);

    constructor() {
        effect(() => this.updateText(this.dt(), this.live(), this.noTime(), this.absolute()));
        effect(() => {
            const timer = this.timer();
            this.clearSubscription();
            if (timer > 0) {
                this.timerSubscription = setTimeout(() => this.updateText(untracked(this.dt)), timer * 1000);
            }
        });
        this._destroy.onDestroy(() => this.clearSubscription());
    }

    updateText(dt?: number | Date, live?: boolean, noTime?: boolean, abs?: boolean): void {
        if (!dt) {
            this.text.set('');
            this.title.set('');
            return;
        }

        dt = new Date(dt);
        if (!dt || isNaN(dt.getTime())) {
            this.text.set('');
            this.title.set('');
            return;
        }

        if (abs) {
            this.text.set(dt.getDate() + '.' + (dt.getMonth() + 1) + '.' + dt.getFullYear() + (noTime ? '' : ' ' + dt.getHours() + ':' + dt.getMinutes()));
            this.title.set(noTime ? '' : dt.getDate() + '.' + (dt.getMonth() + 1) + '.' + dt.getFullYear() + ' ' + dt.getHours() + ':' + dt.getMinutes());
            return;
        }

        this.title.set(dt.getDate() + '.' + (dt.getMonth() + 1) + '.' + dt.getFullYear() + ' ' + dt.getHours() + ':' + dt.getMinutes());

        const now = new Date();
        let nd = now.getTime() / 1000;
        if (noTime) {
            dt = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
            nd = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000;
        }
        const ad = nd - dt.getTime() / 1000;
        const delta = Math.abs(ad);
        const future = ad <= 0;

        let timer = 0;
        if (delta < 60 && !noTime) {
            this.text.set('только что');
            timer = 61 - delta;
        }
        else if (delta < 3600 && !noTime) {
            const v = Math.trunc(delta / 60);
            this.text.set((future ? 'через ' : '') + (v > 1 ? v + ' ' : '') + 'минут' + rus(v, ['', 'у', 'ы', 'ы', 'ы', '']) + (future ? '' : ' назад'));
            timer = 60;
        }
        else if (delta < 86400) {
            const v = Math.trunc(delta / 3600);
            this.text.set(noTime ? 'сегодня' : (v > 1 ? v + ' ' : '') + 'час' + rus(v, ['ов', '', 'а', 'а', 'а', 'ов']) + (future ? '' : ' назад'));
            timer = 3600;
        }
        else if (delta < 86400 * 30) {
            const v = Math.trunc(delta / 86400);
            if (v == 1) {
                this.text.set(future ? 'завтра' : 'вчера');
            }
            else if (v == 2) {
                this.text.set(future ? 'послезавтра' : 'позавчера');
            }
            else {
                this.text.set((future ? 'через ' : '') + v + ' ' + rus(v, ['дней', 'день', 'дня', 'дня', 'дня', 'дней']) + (future ? '' : ' назад'));
            }
            timer = 3600 * 6;
        }
        else if (delta < 86400 * 365) {
            const v = Math.trunc(delta / 2592000);
            this.text.set((future ? 'через ' : '') + (v > 1 ? v + ' ' : '') + 'месяц' + rus(v, ['ев', '', 'а', 'а', 'а', 'ев']) + (future ? '' : ' назад'));
        }
        else {
            this.text.set(future ? 'через год и более' : 'более года назад');
        }
        this.timer.set(live ? timer : 0);
    }

    clearSubscription(): void {
        if (this.timerSubscription !== undefined) {
            clearTimeout(this.timerSubscription);
            this.timerSubscription = undefined;
        }
    }

}
